import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {NgForm} from "@angular/forms";
import {License} from "../../../shared/interface/license";
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {Profile} from "../../../shared/interface/profile";
import {Device} from "../../../shared/interface/device";
import {MatDialog} from "@angular/material/dialog";
import {FilterService} from "../../../shared/services/filter.service";
import {ActivatedRoute, Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "../../../shared/services/auth.service";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {LicenseService} from "../../../shared/services/license.service";
import {delay, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {LicenseAddDialogComponent} from "../license-add-dialog/license-add-dialog.component";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {DeviceAddDialogComponent} from "../device-add-dialog/device-add-dialog.component";
import {DYNAMIC_DICTIONARY_DEVICE_TYPE} from "../../../shared/services/dict.service";

@Component({
  selector: 'app-all-device-list',
  templateUrl: './all-device-list.component.html',
  styleUrls: ['./all-device-list.component.scss']
})
export class AllDeviceListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @Input() disableLinks = false;
  @Input() embeddedView = false;
  @Output() changed: EventEmitter<boolean> = new EventEmitter<boolean>();

  readonly DYNAMIC_DICTIONARY_DEVICE_TYPE = DYNAMIC_DICTIONARY_DEVICE_TYPE;

  initialFilters: any;
  displayedColumns: string[] = [
    'typeOfDevice',
    'name',
    'serialNumber',
    'extensionNumber',
    'note',
    'user',
  ];
  dataSource: Device[] = [];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  profile: Profile;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialog: MatDialog,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private snack: MatSnackBar,
    private auth: AuthService,
    private store: Store<StoreRootState>,
    private router: Router,
    private licenseService: LicenseService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.licenseService.getDeviceListSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: CountedAndPaginated<Device>) => {
        this.dataSource = result.items;
        this.totalData = result.pagination.total;
      });
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      typeOfDevice: 'int',
      username: 'string',
      contains$name: 'string',
      contains$serialNumber: 'string',
      contains$extensionNumber: 'string',
      user: 'string'
    }, filters);
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.licenseService.loadDeviceList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters,
    );
  }

  filter(): void {
    const formValues = {...this.filtersForm.value};
    for (const key in formValues) {
      if (formValues[key] === undefined) {
        delete formValues[key];
      }
    }

    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path,
        this.filterService.parseFiltersToQueryParams({
          page: 0,
          pageSize: this.paginator.pageSize,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          ...formValues
        })
      ]);
    }
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }]);
  }

  addDevice(): void {
    const dialogRef = this.dialog.open(DeviceAddDialogComponent, {
      width: '90vh',
      height: '45vh',
      maxHeight: '90%',
      panelClass: 'full-height-dialog',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadListData();
      this.changed.emit(true);
      this.dialog.closeAll();
    });
  }

  downloadReport(): void {
    this.licenseService.downloadDeviceReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  buildFilters(): any {
    const filters = { ...this.initialFilters };
    return filters;
  }

  delete(license: License): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć licencję?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.licenseService.delete(license.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto licencję', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }

}
